import { ref, computed, watch } from 'vue';
import { getJobProfileVersions } from '@/api/jobProfile';
import { getHTMLDiffCombined, joinAndDiffBlocks } from '@/helpers/diff';

const joinBlocksHtml = (blocks) => (blocks || []).map((block) => block.html).join('');

export const profileSubtitle = (profile) => {
  if (!profile) return '';

  const subtitleParts = [profile.location, profile.job_code].filter(Boolean);

  return subtitleParts.join(subtitleParts.length > 1 ? ' - ' : '') || '—';
};

const buildDiffFields = (oldVersion, newVersion) => {
  if (!oldVersion || !newVersion) return [];

  const oldFields = oldVersion.fields || [];
  const newFields = newVersion.fields || [];
  const maxFieldLength = Math.max(oldFields.length, newFields.length);

  return Array.from({ length: maxFieldLength }, (_, fieldIndex) => {
    const oldFieldRow = oldFields[fieldIndex] || { blocks: [], header: [], footer: [] };
    const newFieldRow = newFields[fieldIndex] || { blocks: [], header: [], footer: [] };

    return {
      ...oldFieldRow,
      type: oldFieldRow.type || newFieldRow.type || `field-${fieldIndex}`,
      label: oldFieldRow.label || newFieldRow.label,
      html: joinAndDiffBlocks(oldFieldRow.blocks || [], newFieldRow.blocks || []),
      headerHtml: joinAndDiffBlocks(oldFieldRow.header || [], newFieldRow.header || []),
      footerHtml: joinAndDiffBlocks(oldFieldRow.footer || [], newFieldRow.footer || []),
    };
  });
};

export const useJobProfileCompare = ({
  active,
  oldProfileUlid,
  oldVersionUlid,
  newProfileUlid,
  newVersionUlid,
}) => {
  const loading = ref(false);
  const loadError = ref(null);
  const oldProfile = ref(null);
  const newProfile = ref(null);
  const oldProfileVersions = ref([]);
  const newProfileVersions = ref([]);

  const load = async () => {
    loadError.value = null;
    oldProfile.value = null;
    newProfile.value = null;
    oldProfileVersions.value = [];
    newProfileVersions.value = [];

    if (!oldProfileUlid.value || !oldVersionUlid.value || !newProfileUlid.value || !newVersionUlid.value) {
      loadError.value = 'Missing job profile version information.';

      return;
    }

    loading.value = true;

    try {
      if (oldProfileUlid.value === newProfileUlid.value) {
        const { versions = [] } = await getJobProfileVersions({ ulid: oldProfileUlid.value });

        oldProfileVersions.value = versions;
        newProfileVersions.value = versions;

        oldProfile.value = versions.find((version) => version.ulid === oldVersionUlid.value);
        newProfile.value = versions.find((version) => version.ulid === newVersionUlid.value);
      } else {
        const [oldResult, newResult] = await Promise.all([
          getJobProfileVersions({ ulid: oldProfileUlid.value }),
          getJobProfileVersions({ ulid: newProfileUlid.value }),
        ]);

        const oldVersions = oldResult?.versions || [];
        const newVersions = newResult?.versions || [];

        oldProfileVersions.value = oldVersions;
        newProfileVersions.value = newVersions;

        oldProfile.value = oldVersions.find((version) => version.ulid === oldVersionUlid.value);
        newProfile.value = newVersions.find((version) => version.ulid === newVersionUlid.value);
      }

      if (!oldProfile.value || !newProfile.value) {
        loadError.value = 'Could not load one or both job profile versions.';
      }
    } catch (error) {
      loadError.value = error?.message || 'Failed to load job profile versions.';
    } finally {
      loading.value = false;
    }
  };

  watch(
    () => [
      active.value,
      oldProfileUlid.value,
      oldVersionUlid.value,
      newProfileUlid.value,
      newVersionUlid.value,
    ],
    () => {
      if (active.value) {
        load();
      }
    },
    { immediate: true },
  );

  const titleDiffHtml = computed(() => {
    if (!oldProfile.value || !newProfile.value) return '';

    return getHTMLDiffCombined(
      oldProfile.value.title || '',
      newProfile.value.title || '',
    );
  });

  const diffFields = computed(() => buildDiffFields(oldProfile.value, newProfile.value));

  const newFieldsPlain = computed(() => {
    if (!newProfile.value?.fields) return [];

    return newProfile.value.fields.map((field) => ({
      ...field,
      html: joinBlocksHtml(field.blocks),
      headerHtml: joinBlocksHtml(field.header),
      footerHtml: joinBlocksHtml(field.footer),
    }));
  });

  const oldFieldsPlain = computed(() => {
    if (!oldProfile.value?.fields) return [];

    return oldProfile.value.fields.map((field) => ({
      ...field,
      html: joinBlocksHtml(field.blocks),
      headerHtml: joinBlocksHtml(field.header),
      footerHtml: joinBlocksHtml(field.footer),
    }));
  });

  const oldVersionIsCurrentVersion = computed(() => {
    const vers = oldProfileVersions.value;

    if (!vers?.length || !oldProfile.value) return false;

    return vers[0]?.ulid === oldProfile.value.ulid;
  });

  const newVersionIsCurrentVersion = computed(() => {
    const vers = newProfileVersions.value;

    if (!vers?.length || !newProfile.value) return false;

    return vers[0]?.ulid === newProfile.value.ulid;
  });

  const oldProfileSubtitle = computed(() => profileSubtitle(oldProfile.value));
  const newProfileSubtitle = computed(() => profileSubtitle(newProfile.value));

  return {
    loading,
    loadError,
    oldProfile,
    newProfile,
    titleDiffHtml,
    diffFields,
    newFieldsPlain,
    oldFieldsPlain,
    oldVersionIsCurrentVersion,
    newVersionIsCurrentVersion,
    oldProfileSubtitle,
    newProfileSubtitle,
  };
};
